import {ChangeEvent, useState} from "react";
import {SelectField, styled} from "@reside/ui";
import {Formik} from "formik";
import {useTemplateNamesQuery} from "./hooks/useTemplateNamesQuery";
import {Spinner} from "../../atoms/spinner";
import {ButtonLink} from "../../atoms/button-link";
import {Tooltip} from "../../atoms/tooltip";
import {useEventTriggersQuery} from "./hooks/useEventTriggersQuery";
import {Column, ListTable} from "../../atoms/list-table";
import {EventTrigger} from "../../services/HubAndSpokeService";
import {getWorkflowLabel} from "../../atoms/start-workflow/workflowMapping";
import {HubAndSpokePage} from "./atoms/HubAndSpokePage";
import {ButtonsWrapper, HeaderFilter, TableContainer} from "./styles";
import {getUploadOnLabel} from "./consts";

const EVENT_TRIGGERS_PATH = "/admin/hub-and-spoke/event-triggers";
/** Non-empty sentinel: SelectField treats "" as "nothing selected". */
const ALL_TEMPLATES = "__all__";
/** Facilities beyond this count are collapsed into "+N more" with a tooltip. */
const MAX_LISTED_FACILITIES = 2;

type Row = {
  rowData: EventTrigger;
};

const FacilitiesCell = ({
  facilities,
}: {
  facilities: EventTrigger["facilities"];
}) => {
  const names = facilities.map(facility => facility.name);

  if (names.length <= MAX_LISTED_FACILITIES) {
    return <p>{names.join(", ")}</p>;
  }

  return (
    <Tooltip content={names.join(", ")}>
      <p>
        {names.slice(0, MAX_LISTED_FACILITIES).join(", ")}{" "}
        <MoreLabel>+{names.length - MAX_LISTED_FACILITIES} more</MoreLabel>
      </p>
    </Tooltip>
  );
};

export const EventTriggers = () => {
  const [templateName, setTemplateName] = useState<string>();

  const {isLoading: isLoadingTemplateNames, data: templateNames = []} =
    useTemplateNamesQuery();
  const {isLoading: isLoadingEventTriggers, data: eventTriggers = []} =
    useEventTriggersQuery({templateName});

  return (
    <HubAndSpokePage
      title="Event Triggers"
      actions={
        <>
          <HeaderFilter>
            <Formik
              initialValues={{"template-name": ALL_TEMPLATES}}
              onSubmit={() => undefined}
            >
              <SelectField
                label="Template"
                name="template-name"
                disabled={isLoadingTemplateNames}
                options={[
                  {label: "All templates", value: ALL_TEMPLATES},
                  ...templateNames.map(template => ({
                    label: template,
                    value: template,
                  })),
                ]}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                  const value = event.target.value;
                  setTemplateName(value === ALL_TEMPLATES ? undefined : value);
                }}
              />
            </Formik>
          </HeaderFilter>
          <ButtonLink
            to={`${EVENT_TRIGGERS_PATH}/create`}
            color="primary"
            outline={false}
          >
            New Event Trigger
          </ButtonLink>
        </>
      }
    >
      {isLoadingEventTriggers ? (
        <Spinner />
      ) : (
        <TableContainer>
          <ListTable
            key="event-triggers"
            data={
              Array.isArray(eventTriggers) ? eventTriggers : [eventTriggers]
            }
            noRowsMessage={
              templateName
                ? "No event triggers for this template."
                : "No event triggers yet."
            }
          >
            <Column
              cellRenderer={({rowData}: Row) => (
                <>
                  <p>{rowData.template_field.template_name}</p>
                  <SecondaryText>
                    {getWorkflowLabel(rowData.template_field.workflow_type)}
                  </SecondaryText>
                </>
              )}
              label="Template"
              width={200}
            />
            <Column
              cellRenderer={({rowData}: Row) => (
                <p>{rowData.template_field.friendly_name}</p>
              )}
              label="Field"
              width={200}
            />
            <Column
              cellRenderer={({rowData}: Row) => <p>{rowData.document}</p>}
              label="Document"
              width={200}
            />
            <Column
              cellRenderer={({rowData}: Row) => (
                <FacilitiesCell facilities={rowData.facilities} />
              )}
              label="Facilities"
            />
            <Column
              cellRenderer={({rowData}: Row) => (
                <>
                  <p>{rowData.sftp_config.name}</p>
                  <SecondaryText>{rowData.sftp_config.hostname}</SecondaryText>
                </>
              )}
              label="SFTP"
              width={180}
            />
            <Column
              cellRenderer={({rowData}: Row) => (
                <p>{rowData.upload_on.map(getUploadOnLabel).join(", ")}</p>
              )}
              label="Upload On"
              width={150}
            />
            <Column
              cellRenderer={({rowData: {id}}: Row) => (
                <ButtonsWrapper>
                  <ButtonLink to={`${EVENT_TRIGGERS_PATH}/${id}`}>
                    View
                  </ButtonLink>
                  <ButtonLink
                    to={`${EVENT_TRIGGERS_PATH}/edit/${id}`}
                    color="primary"
                  >
                    Edit
                  </ButtonLink>
                </ButtonsWrapper>
              )}
              width={170}
            />
          </ListTable>
        </TableContainer>
      )}
    </HubAndSpokePage>
  );
};

const SecondaryText = styled.p`
  font-size: ${({theme}) => theme.fontSize[1]}px;
  color: ${({theme}) => theme.color.gray150};
`;

const MoreLabel = styled.span`
  color: ${({theme}) => theme.color.primary};
  white-space: nowrap;
`;
